import { t } from "i18next";
/** No upload progress or stage change for this long counts as a stalled upload. */
export const UPLOAD_STALL_TIMEOUT_MS = 60_000;
/** Longest wait for a JSON API request that confirms a visit or upload. */
export const REQUEST_TIMEOUT_MS = 60_000;

export class UploadStalledError extends Error {
  constructor(message = t("backgroundVisitUpload.errors.stalled")) {
    super(message);
    this.name = "UploadStalledError";
  }
}

export class RequestTimeoutError extends Error {
  constructor(message = t("backgroundVisitUpload.errors.timeout")) {
    super(message);
    this.name = "RequestTimeoutError";
  }
}

/**
 * Runs `task` and rejects with {@link UploadStalledError} when it reports no
 * activity (`touch`) for `stallMs`. The task's signal is aborted on stall or
 * when the outer signal aborts, so the underlying request is released.
 */
export const runWithStallTimeout = <T>(
  task: (context: { signal: AbortSignal; touch: () => void }) => Promise<T>,
  {
    stallMs = UPLOAD_STALL_TIMEOUT_MS,
    signal,
  }: { stallMs?: number; signal?: AbortSignal } = {},
): Promise<T> => {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let rejectStall: (error: Error) => void = () => {};
  const stalled = new Promise<never>((_, reject) => {
    rejectStall = reject;
  });
  const touch = (): void => {
    if (controller.signal.aborted) return;
    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(() => {
      const error = new UploadStalledError();
      rejectStall(error);
      controller.abort(error);
    }, stallMs);
  };
  const forwardAbort = (): void => controller.abort(signal?.reason);
  if (signal?.aborted) forwardAbort();
  else signal?.addEventListener("abort", forwardAbort, { once: true });
  touch();
  let run: Promise<T>;
  try {
    run = task({ signal: controller.signal, touch });
  } catch (error) {
    run = Promise.reject(error);
  }
  return Promise.race([run, stalled]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    signal?.removeEventListener("abort", forwardAbort);
    // A task that settles after a stall must not surface as unhandled.
    run.catch(() => undefined);
  });
};

/** Returns a signal that aborts with {@link RequestTimeoutError} after `ms`, plus a cleanup. */
export const timeoutSignal = (
  ms = REQUEST_TIMEOUT_MS,
  signal?: AbortSignal,
): { signal: AbortSignal; clear: () => void } => {
  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(new RequestTimeoutError()),
    ms,
  );
  const forwardAbort = (): void => controller.abort(signal?.reason);
  if (signal?.aborted) forwardAbort();
  else signal?.addEventListener("abort", forwardAbort, { once: true });
  return {
    signal: controller.signal,
    clear: () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", forwardAbort);
    },
  };
};

/** Maps an abort caused by {@link timeoutSignal} to its readable error. */
export const withRequestTimeout = async <T>(
  run: (signal: AbortSignal) => Promise<T>,
  {
    ms = REQUEST_TIMEOUT_MS,
    signal,
  }: { ms?: number; signal?: AbortSignal } = {},
): Promise<T> => {
  const timeout = timeoutSignal(ms, signal);
  let timedOut: (error: Error) => void = () => {};
  const expired = new Promise<never>((_, reject) => {
    timedOut = reject;
  });
  const onAbort = (): void => {
    if (timeout.signal.reason instanceof RequestTimeoutError)
      timedOut(timeout.signal.reason);
  };
  timeout.signal.addEventListener("abort", onAbort, { once: true });
  let pending: Promise<T>;
  try {
    pending = run(timeout.signal);
  } catch (error) {
    pending = Promise.reject(error);
  }
  try {
    return await Promise.race([pending, expired]);
  } finally {
    timeout.clear();
    timeout.signal.removeEventListener("abort", onAbort);
    pending.catch(() => undefined);
  }
};
